import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import { makeStyles } from '../../../utils/hooks/makeStyles'
import { toast } from 'react-toastify'
import {
  CreateENTApi,
  GetENTApi,
} from '../../../services/CampManagementService'

interface ENTData {
  chiefComplaints: {
    observationId?: string
    earPain: boolean
    earDischarge: boolean
    hearingLoss: boolean
    tinnitus: boolean
    vertigo: boolean
    nasalBlock: boolean
    nasalDischarge: boolean
    sneezing: boolean
    epistaxis: boolean
    lossOfSmell: boolean
    throatPain: boolean
    dysphagia: boolean
    hoarsenessOfVoice: boolean
    foreignBodySensation: boolean
    asymptomatic: boolean
    duration: string
  }
  relevantHistory: {
    observationId?: string
    diabetes: boolean
    hypertension: boolean
    allergy: boolean
    recurrentUrti: boolean
    smoking: boolean
    alcohol: boolean
    previousEntSurgery: boolean
    trauma: boolean
    nilSignificant: boolean
  }
  clinicalExamination: {
    observationId?: string
    earNormal: boolean
    earWax: boolean
    earCongestion: boolean
    earDischarge: boolean
    tmPerforation: boolean
    septumCentral: boolean
    dns: boolean
    turbinateHypertrophy: boolean
    noseDischarge: boolean
    polyps: boolean
    throatNormal: boolean
    throatCongestion: boolean
    tonsillarEnlargement: boolean
    ulcerLesion: boolean
  }
  hearingAssessment: {
    observationId?: string
    normal: boolean
    reduced: boolean
    tuningForkTestDone: boolean
    audiometryAdvised: boolean
  }
  provisionalDiagnosis: {
    observationId?: string
    impactedWax: boolean
    otitisExterna: boolean
    otitisMedia: boolean
    allergicRhinitis: boolean
    sinusitis: boolean
    pharyngitis: boolean
    tonsillitis: boolean
    laryngitis: boolean
    dns: boolean
    other: string
  }
  advicePlan: {
    observationId?: string
    medications: boolean
    earDrops: boolean
    nasalSpray: boolean
    steamInhalation: boolean
    audiometry: boolean
    endoscopy: boolean
    referralHigherCentre: boolean
    surgeryAdvised: boolean
    reviewAfterDays: string
  }
}

const useStyles = makeStyles(() => ({
  container: {
    padding: 24,
  },
  section: {
    marginBottom: 24,
    marginTop: 24,
  },
  sectionHeader: {
    marginBottom: 16,
    fontWeight: 600,
    color: '#1976d2',
  },
  subsectionHeader: {
    marginBottom: 12,
    marginTop: 16,
    fontWeight: 500,
    color: '#333',
  },
  divider: {
    marginTop: 32,
    marginBottom: 32,
  },
  durationContainer: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  durationSelect: {
    minWidth: 150,
  },
  otherInput: {
    minWidth: 300,
  },
  reviewInput: {
    width: 120,
  },
  submitWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
}))

function ENTScreening({ appointmentData }: any) {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)

  const [data, setData] = useState<ENTData>({
    chiefComplaints: {
      earPain: false,
      earDischarge: false,
      hearingLoss: false,
      tinnitus: false,
      vertigo: false,
      nasalBlock: false,
      nasalDischarge: false,
      sneezing: false,
      epistaxis: false,
      lossOfSmell: false,
      throatPain: false,
      dysphagia: false,
      hoarsenessOfVoice: false,
      foreignBodySensation: false,
      asymptomatic: false,
      duration: '',
    },
    relevantHistory: {
      diabetes: false,
      hypertension: false,
      allergy: false,
      recurrentUrti: false,
      smoking: false,
      alcohol: false,
      previousEntSurgery: false,
      trauma: false,
      nilSignificant: false,
    },
    clinicalExamination: {
      earNormal: false,
      earWax: false,
      earCongestion: false,
      earDischarge: false,
      tmPerforation: false,
      septumCentral: false,
      dns: false,
      turbinateHypertrophy: false,
      noseDischarge: false,
      polyps: false,
      throatNormal: false,
      throatCongestion: false,
      tonsillarEnlargement: false,
      ulcerLesion: false,
    },
    hearingAssessment: {
      normal: false,
      reduced: false,
      tuningForkTestDone: false,
      audiometryAdvised: false,
    },
    provisionalDiagnosis: {
      impactedWax: false,
      otitisExterna: false,
      otitisMedia: false,
      allergicRhinitis: false,
      sinusitis: false,
      pharyngitis: false,
      tonsillitis: false,
      laryngitis: false,
      dns: false,
      other: '',
    },
    advicePlan: {
      medications: false,
      earDrops: false,
      nasalSpray: false,
      steamInhalation: false,
      audiometry: false,
      endoscopy: false,
      referralHigherCentre: false,
      surgeryAdvised: false,
      reviewAfterDays: '',
    },
  })

  // Fetch existing ENT data on component mount
  useEffect(() => {
    fetchENTData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentData?.visitId])

  const fetchENTData = async () => {
    if (appointmentData?.visitId) {
      try {
        const response = await GetENTApi(appointmentData?.visitId)
        if (response.success && response.data) {
          const entData = response.data
          setData((prevData: any) => ({
            chiefComplaints:
              entData?.chiefComplaints || prevData?.chiefComplaints,
            relevantHistory:
              entData?.relevantHistory || prevData?.relevantHistory,
            clinicalExamination:
              entData?.clinicalExamination || prevData?.clinicalExamination,
            hearingAssessment:
              entData?.hearingAssessment || prevData?.hearingAssessment,
            provisionalDiagnosis:
              entData?.provisionalDiagnosis || prevData?.provisionalDiagnosis,
            advicePlan: entData?.advicePlan || prevData?.advicePlan,
          }))
        }
      } catch (error) {
        console.error('Error fetching ENT data:', error)
      }
    }
  }

  const handleChiefComplaintChange =
    (field: keyof ENTData['chiefComplaints']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        chiefComplaints: {
          ...data?.chiefComplaints,
          [field]: event?.target?.checked,
        },
      })
    }

  // const handleDurationChange = (event: any) => {
  //   setData({
  //     ...data,
  //     chiefComplaints: {
  //       ...data?.chiefComplaints,
  //       duration: event?.target?.value,
  //     },
  //   })
  // }

  const handleHistoryChange =
    (field: keyof ENTData['relevantHistory']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        relevantHistory: {
          ...data?.relevantHistory,
          [field]: event?.target?.checked,
        },
      })
    }

  const handleClinicalExamChange =
    (field: keyof ENTData['clinicalExamination']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        clinicalExamination: {
          ...data?.clinicalExamination,
          [field]: event?.target?.checked,
        },
      })
    }

  const handleHearingChange =
    (field: keyof ENTData['hearingAssessment']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        hearingAssessment: {
          ...data?.hearingAssessment,
          [field]: event?.target?.checked,
        },
      })
    }

  const handleDiagnosisChange =
    (field: keyof ENTData['provisionalDiagnosis']) => (event: any) => {
      const value =
        event?.target?.type === 'checkbox'
          ? event?.target?.checked
          : event?.target?.value
      setData({
        ...data,
        provisionalDiagnosis: {
          ...data?.provisionalDiagnosis,
          [field]: value,
        },
      })
    }

  const handleAdviceChange =
    (field: keyof ENTData['advicePlan']) => (event: any) => {
      const value =
        event?.target?.type === 'checkbox'
          ? event?.target?.checked
          : event?.target?.value
      setData({
        ...data,
        advicePlan: {
          ...data?.advicePlan,
          [field]: value,
        },
      })
    }

  const handleSubmit = async () => {
    setLoading(true)

    try {
      const payload: any = {
        patientDetails: {
          name: appointmentData?.name || '',
          age: appointmentData?.age || 0,
          sex: appointmentData?.gender?.toUpperCase().startsWith('M')
            ? 'M'
            : 'F',
          campIdOrTokenNo: appointmentData?.campId || '',
          date: new Date().toISOString().split('T')[0],
          mobileNo: appointmentData?.mobile || '',
        },
        chiefComplaints: {
          ...(data?.chiefComplaints?.observationId && {
            observationId: data?.chiefComplaints?.observationId,
          }),
          earPain: data?.chiefComplaints?.earPain,
          earDischarge: data?.chiefComplaints?.earDischarge,
          hearingLoss: data?.chiefComplaints?.hearingLoss,
          tinnitus: data?.chiefComplaints?.tinnitus,
          vertigo: data?.chiefComplaints?.vertigo,
          nasalBlock: data?.chiefComplaints?.nasalBlock,
          nasalDischarge: data?.chiefComplaints?.nasalDischarge,
          sneezing: data?.chiefComplaints?.sneezing,
          epistaxis: data?.chiefComplaints?.epistaxis,
          lossOfSmell: data?.chiefComplaints?.lossOfSmell,
          throatPain: data?.chiefComplaints?.throatPain,
          dysphagia: data?.chiefComplaints?.dysphagia,
          hoarsenessOfVoice: data?.chiefComplaints?.hoarsenessOfVoice,
          foreignBodySensation: data?.chiefComplaints?.foreignBodySensation,
          asymptomatic: data?.chiefComplaints?.asymptomatic,
          duration: data?.chiefComplaints?.duration || null,
        },
        relevantHistory: {
          ...(data?.relevantHistory?.observationId && {
            observationId: data?.relevantHistory?.observationId,
          }),
          diabetes: data?.relevantHistory?.diabetes,
          hypertension: data?.relevantHistory?.hypertension,
          allergy: data?.relevantHistory?.allergy,
          recurrentUrti: data?.relevantHistory?.recurrentUrti,
          smoking: data?.relevantHistory?.smoking,
          alcohol: data?.relevantHistory?.alcohol,
          previousEntSurgery: data?.relevantHistory?.previousEntSurgery,
          trauma: data?.relevantHistory?.trauma,
          nilSignificant: data?.relevantHistory?.nilSignificant,
        },
        clinicalExamination: {
          ...(data?.clinicalExamination?.observationId && {
            observationId: data?.clinicalExamination?.observationId,
          }),
          earNormal: data?.clinicalExamination?.earNormal,
          earWax: data?.clinicalExamination?.earWax,
          earCongestion: data?.clinicalExamination?.earCongestion,
          earDischarge: data?.clinicalExamination?.earDischarge,
          tmPerforation: data?.clinicalExamination?.tmPerforation,
          septumCentral: data?.clinicalExamination?.septumCentral,
          dns: data?.clinicalExamination?.dns,
          turbinateHypertrophy: data?.clinicalExamination?.turbinateHypertrophy,
          noseDischarge: data?.clinicalExamination?.noseDischarge,
          polyps: data?.clinicalExamination?.polyps,
          throatNormal: data?.clinicalExamination?.throatNormal,
          throatCongestion: data?.clinicalExamination?.throatCongestion,
          tonsillarEnlargement: data?.clinicalExamination?.tonsillarEnlargement,
          ulcerLesion: data?.clinicalExamination?.ulcerLesion,
        },
        hearingAssessment: {
          ...(data?.hearingAssessment?.observationId && {
            observationId: data?.hearingAssessment?.observationId,
          }),
          normal: data?.hearingAssessment?.normal,
          reduced: data?.hearingAssessment?.reduced,
          tuningForkTestDone: data?.hearingAssessment?.tuningForkTestDone,
          audiometryAdvised: data?.hearingAssessment?.audiometryAdvised,
        },
        provisionalDiagnosis: {
          ...(data?.provisionalDiagnosis?.observationId && {
            observationId: data?.provisionalDiagnosis?.observationId,
          }),
          impactedWax: data?.provisionalDiagnosis?.impactedWax,
          otitisExterna: data?.provisionalDiagnosis?.otitisExterna,
          otitisMedia: data?.provisionalDiagnosis?.otitisMedia,
          allergicRhinitis: data?.provisionalDiagnosis?.allergicRhinitis,
          sinusitis: data?.provisionalDiagnosis?.sinusitis,
          pharyngitis: data?.provisionalDiagnosis?.pharyngitis,
          tonsillitis: data?.provisionalDiagnosis?.tonsillitis,
          laryngitis: data?.provisionalDiagnosis?.laryngitis,
          dns: data?.provisionalDiagnosis?.dns,
          other: data?.provisionalDiagnosis?.other || null,
        },
        advicePlan: {
          ...(data?.advicePlan?.observationId && {
            observationId: data?.advicePlan?.observationId,
          }),
          medications: data?.advicePlan?.medications,
          earDrops: data?.advicePlan?.earDrops,
          nasalSpray: data?.advicePlan?.nasalSpray,
          steamInhalation: data?.advicePlan?.steamInhalation,
          audiometry: data?.advicePlan?.audiometry,
          endoscopy: data?.advicePlan?.endoscopy,
          referralHigherCentre: data?.advicePlan?.referralHigherCentre,
          surgeryAdvised: data?.advicePlan?.surgeryAdvised,
          reviewAfterDays: parseInt(data?.advicePlan?.reviewAfterDays) || 0,
        },
        doctorDetails: {
          name: appointmentData?.visitingDoctorName || '',
          regNo: appointmentData?.visitingDoctorId || '',
        },
      }

      const response: any = await CreateENTApi(
        payload,
        appointmentData?.visitId,
        appointmentData?.fhirId,
        appointmentData?.visitingDoctorId
      )

      if (response?.success) {
        toast.success('ENT screening submitted successfully')
        fetchENTData()
      } else {
        toast.error(response?.message || 'Failed to submit screening')
      }
    } catch (error: any) {
      console.error('Error submitting ENT screening:', error)
      toast.error(error?.message || 'An error occurred while submitting')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box className={classes.container}>
      {/* Chief Complaints Section */}
      <Typography variant='h5' className={classes.sectionHeader}>
        Chief Complaints
      </Typography>

      {/* Ear Complaints */}
      <Typography variant='h6' className={classes.subsectionHeader}>
        Ear
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.earPain}
              onChange={handleChiefComplaintChange('earPain')}
            />
          }
          label='Ear Pain'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.earDischarge}
              onChange={handleChiefComplaintChange('earDischarge')}
            />
          }
          label='Ear Discharge'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.hearingLoss}
              onChange={handleChiefComplaintChange('hearingLoss')}
            />
          }
          label='Hearing Loss'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.tinnitus}
              onChange={handleChiefComplaintChange('tinnitus')}
            />
          }
          label='Tinnitus'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.vertigo}
              onChange={handleChiefComplaintChange('vertigo')}
            />
          }
          label='Vertigo'
        />
      </FormGroup>

      {/* Nose Complaints */}
      <Typography variant='h6' className={classes.subsectionHeader}>
        Nose
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.nasalBlock}
              onChange={handleChiefComplaintChange('nasalBlock')}
            />
          }
          label='Nasal Block'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.nasalDischarge}
              onChange={handleChiefComplaintChange('nasalDischarge')}
            />
          }
          label='Nasal Discharge'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.sneezing}
              onChange={handleChiefComplaintChange('sneezing')}
            />
          }
          label='Sneezing'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.epistaxis}
              onChange={handleChiefComplaintChange('epistaxis')}
            />
          }
          label='Epistaxis'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.lossOfSmell}
              onChange={handleChiefComplaintChange('lossOfSmell')}
            />
          }
          label='Loss of Smell'
        />
      </FormGroup>

      {/* Throat Complaints */}
      <Typography variant='h6' className={classes.subsectionHeader}>
        Throat
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.throatPain}
              onChange={handleChiefComplaintChange('throatPain')}
            />
          }
          label='Throat Pain'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.dysphagia}
              onChange={handleChiefComplaintChange('dysphagia')}
            />
          }
          label='Dysphagia'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.hoarsenessOfVoice}
              onChange={handleChiefComplaintChange('hoarsenessOfVoice')}
            />
          }
          label='Hoarseness of Voice'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.foreignBodySensation}
              onChange={handleChiefComplaintChange('foreignBodySensation')}
            />
          }
          label='Foreign Body Sensation'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.chiefComplaints?.asymptomatic}
              onChange={handleChiefComplaintChange('asymptomatic')}
            />
          }
          label='Asymptomatic'
        />
      </FormGroup>

      {/*  <Box className={classes.section}>
       <Box className={classes.durationContainer}>
          <Typography>Duration:</Typography>
          <TextField
            placeholder='e.g., 5 days, 2 weeks'
            value={data?.chiefComplaints?.duration}
            onChange={handleDurationChange}
            size='small'
            className={classes.durationSelect}
          />
        </Box> 
      </Box>*/}

      <Divider className={classes.divider} />

      {/* Relevant History Section */}
      <Typography variant='h5' className={classes.sectionHeader}>
        Relevant History
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.diabetes}
              onChange={handleHistoryChange('diabetes')}
            />
          }
          label='Diabetes'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.hypertension}
              onChange={handleHistoryChange('hypertension')}
            />
          }
          label='Hypertension'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.allergy}
              onChange={handleHistoryChange('allergy')}
            />
          }
          label='Allergy'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.recurrentUrti}
              onChange={handleHistoryChange('recurrentUrti')}
            />
          }
          label='Recurrent URTI'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.smoking}
              onChange={handleHistoryChange('smoking')}
            />
          }
          label='Smoking'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.alcohol}
              onChange={handleHistoryChange('alcohol')}
            />
          }
          label='Alcohol'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.previousEntSurgery}
              onChange={handleHistoryChange('previousEntSurgery')}
            />
          }
          label='Previous ENT Surgery'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.trauma}
              onChange={handleHistoryChange('trauma')}
            />
          }
          label='Trauma'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.relevantHistory?.nilSignificant}
              onChange={handleHistoryChange('nilSignificant')}
            />
          }
          label='Nil Significant'
        />
      </FormGroup>

      <Divider className={classes.divider} />

      {/* Clinical Examination Section */}
      <Typography variant='h5' className={classes.sectionHeader}>
        Clinical Examination
      </Typography>

      {/* Ear Examination */}
      <Typography variant='h6' className={classes.subsectionHeader}>
        Ear
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.earNormal}
              onChange={handleClinicalExamChange('earNormal')}
            />
          }
          label='Normal'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.earWax}
              onChange={handleClinicalExamChange('earWax')}
            />
          }
          label='Wax'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.earCongestion}
              onChange={handleClinicalExamChange('earCongestion')}
            />
          }
          label='Congestion'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.earDischarge}
              onChange={handleClinicalExamChange('earDischarge')}
            />
          }
          label='Discharge'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.tmPerforation}
              onChange={handleClinicalExamChange('tmPerforation')}
            />
          }
          label='TM Perforation'
        />
      </FormGroup>

      {/* Nose Examination */}
      <Typography variant='h6' className={classes.subsectionHeader}>
        Nose
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.septumCentral}
              onChange={handleClinicalExamChange('septumCentral')}
            />
          }
          label='Septum Central'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.dns}
              onChange={handleClinicalExamChange('dns')}
            />
          }
          label='DNS'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.turbinateHypertrophy}
              onChange={handleClinicalExamChange('turbinateHypertrophy')}
            />
          }
          label='Turbinate Hypertrophy'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.noseDischarge}
              onChange={handleClinicalExamChange('noseDischarge')}
            />
          }
          label='Discharge'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.polyps}
              onChange={handleClinicalExamChange('polyps')}
            />
          }
          label='Polyps'
        />
      </FormGroup>

      {/* Throat Examination */}
      <Typography variant='h6' className={classes.subsectionHeader}>
        Throat
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.throatNormal}
              onChange={handleClinicalExamChange('throatNormal')}
            />
          }
          label='Normal'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.throatCongestion}
              onChange={handleClinicalExamChange('throatCongestion')}
            />
          }
          label='Congestion'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.tonsillarEnlargement}
              onChange={handleClinicalExamChange('tonsillarEnlargement')}
            />
          }
          label='Tonsillar Enlargement'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.clinicalExamination?.ulcerLesion}
              onChange={handleClinicalExamChange('ulcerLesion')}
            />
          }
          label='Ulcer/Lesion'
        />
      </FormGroup>

      <Divider className={classes.divider} />

      {/* Hearing Assessment Section */}
      <Typography variant='h5' className={classes.sectionHeader}>
        Hearing Assessment
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.hearingAssessment?.normal}
              onChange={handleHearingChange('normal')}
            />
          }
          label='Normal'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.hearingAssessment?.reduced}
              onChange={handleHearingChange('reduced')}
            />
          }
          label='Reduced'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.hearingAssessment?.tuningForkTestDone}
              onChange={handleHearingChange('tuningForkTestDone')}
            />
          }
          label='Tuning Fork Test Done'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.hearingAssessment?.audiometryAdvised}
              onChange={handleHearingChange('audiometryAdvised')}
            />
          }
          label='Audiometry Advised'
        />
      </FormGroup>

      <Divider className={classes.divider} />

      {/* Provisional Diagnosis Section */}
      <Typography variant='h5' className={classes.sectionHeader}>
        Provisional Diagnosis
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.impactedWax}
              onChange={handleDiagnosisChange('impactedWax')}
            />
          }
          label='Impacted Wax'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.otitisExterna}
              onChange={handleDiagnosisChange('otitisExterna')}
            />
          }
          label='Otitis Externa'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.otitisMedia}
              onChange={handleDiagnosisChange('otitisMedia')}
            />
          }
          label='Otitis Media'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.allergicRhinitis}
              onChange={handleDiagnosisChange('allergicRhinitis')}
            />
          }
          label='Allergic Rhinitis'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.sinusitis}
              onChange={handleDiagnosisChange('sinusitis')}
            />
          }
          label='Sinusitis'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.pharyngitis}
              onChange={handleDiagnosisChange('pharyngitis')}
            />
          }
          label='Pharyngitis'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.tonsillitis}
              onChange={handleDiagnosisChange('tonsillitis')}
            />
          }
          label='Tonsillitis'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.laryngitis}
              onChange={handleDiagnosisChange('laryngitis')}
            />
          }
          label='Laryngitis'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.provisionalDiagnosis?.dns}
              onChange={handleDiagnosisChange('dns')}
            />
          }
          label='DNS'
        />
      </FormGroup>
      <Box className={classes.durationContainer}>
        <Typography>Other:</Typography>
        <TextField
          placeholder='Specify other diagnosis'
          value={data?.provisionalDiagnosis?.other}
          onChange={handleDiagnosisChange('other')}
          size='small'
          className={classes.otherInput}
        />
      </Box>

      <Divider className={classes.divider} />

      {/* Advice/Plan Section */}
      <Typography variant='h5' className={classes.sectionHeader}>
        Advice & Plan
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.advicePlan?.medications}
              onChange={handleAdviceChange('medications')}
            />
          }
          label='Medications'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.advicePlan?.earDrops}
              onChange={handleAdviceChange('earDrops')}
            />
          }
          label='Ear Drops'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.advicePlan?.nasalSpray}
              onChange={handleAdviceChange('nasalSpray')}
            />
          }
          label='Nasal Spray'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.advicePlan?.steamInhalation}
              onChange={handleAdviceChange('steamInhalation')}
            />
          }
          label='Steam Inhalation'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.advicePlan?.audiometry}
              onChange={handleAdviceChange('audiometry')}
            />
          }
          label='Audiometry'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.advicePlan?.endoscopy}
              onChange={handleAdviceChange('endoscopy')}
            />
          }
          label='Endoscopy'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.advicePlan?.referralHigherCentre}
              onChange={handleAdviceChange('referralHigherCentre')}
            />
          }
          label='Referral to Higher Centre'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data?.advicePlan?.surgeryAdvised}
              onChange={handleAdviceChange('surgeryAdvised')}
            />
          }
          label='Surgery Advised'
        />
      </FormGroup>
      <Box className={classes.durationContainer}>
        <Typography>Review After (days):</Typography>
        <TextField
          type='number'
          placeholder='Days'
          value={data?.advicePlan?.reviewAfterDays}
          onChange={handleAdviceChange('reviewAfterDays')}
          size='small'
          className={classes.reviewInput}
        />
      </Box>

      <Divider className={classes.divider} />

      {/* Submit Button */}
      <Box className={classes.submitWrapper}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Submit ENT Screening'}
        </Button>
      </Box>
    </Box>
  )
}

export default ENTScreening
